import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Alert, Card, CardContent } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, changeUserPassword } from '../utils/supabaseEdgeApi';
import AddressModal from './AddressModal';
import LoadingOverlay from './LoadingOverlay';

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, setUser, redirectToLogin } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    contact: '',
    address: '',
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    setProfile({
      name: user.name || '',
      email: user.email || '',
      contact: user.contact || '',
      address: user.address || '',
    });
  }, [user, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSave = async () => {
    if (!user?.id || !token) {
      setError('Please login to update your profile.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await updateUserProfile({
        userId: user.id,
        token,
        profile: {
          name: profile.name,
          contact: profile.contact,
          address: profile.address,
        },
      });
      setUser((prev) => prev ? { ...prev, name: profile.name, contact: profile.contact, address: profile.address } : prev);
      setMessage(response.message || 'Profile updated successfully.');
    } catch (fetchError) {
      if (fetchError?.status === 401) {
        redirectToLogin();
        return;
      }
      setError(fetchError.message || 'Unable to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileAddressSave = ({ address }) => {
    setProfile((prev) => ({ ...prev, address: address || prev.address }));
    setAddressModalOpen(false);
  };

  const handlePasswordSave = async () => {
    if (!passwords.currentPassword || !passwords.newPassword) {
      setError('Please fill current and new password.');
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await changeUserPassword({
        userId: user.id,
        token,
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage(response.message || 'Password changed successfully.');
    } catch (fetchError) {
      if (fetchError?.status === 401) {
        redirectToLogin();
        return;
      }
      setError(fetchError.message || 'Unable to change password.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <LoadingOverlay open={loading} message="Saving profile..." />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Profile
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Personal Details
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          <TextField
            label="Name"
            name="name"
            value={profile.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={profile.email}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Contact"
            name="contact"
            value={profile.contact}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={profile.address}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
          <Button
            variant="outlined"
            sx={{ mt: 1, mb: 2 }}
            onClick={() => setAddressModalOpen(true)}
          >
            Add address
          </Button>
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={handleProfileSave}
            disabled={loading}
          >
            Save Profile
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Change Password
          </Typography>
          <TextField
            label="Current Password"
            name="currentPassword"
            type="password"
            value={passwords.currentPassword}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="New Password"
            name="newPassword"
            type="password"
            value={passwords.newPassword}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Confirm New Password"
            name="confirmPassword"
            type="password"
            value={passwords.confirmPassword}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
          />
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handlePasswordSave}
            disabled={loading}
          >
            Change Password
          </Button>
        </CardContent>
      </Card>
      <AddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        initialAddress={profile.address}
        initialLocationLink=""
        initialLat=""
        initialLng=""
        onSave={handleProfileAddressSave}
        userId={user?.id}
        token={token}
        persistAddress={!!token}
        title="Select address"
      />
    </Box>
  );
};

export default Profile;
